import { Calendar, MoreVertical, User } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { GestureResponderEvent } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { getDayJs } from "../../core/dayJs";
import { Permission } from "../../permissions/types";
import { Body } from "../../ui/Body";
import { InfoRow, type InfoRowItemProps } from "../../ui/InfoRow";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";
import { FeedItemManageSheet } from "../feed/ui/FeedItemManageSheet";
import { FeedItemReactionShareSheet } from "../feed/ui/FeedItemReactionsShareSheet";
import { FeedTextInput } from "../feed/ui/FeedTextInput";
import { FeedTextWithMentions } from "../feed/ui/FeedTextWithMentions";
import { useDisplayNameInfo } from "../feed/utils";
import type { BusinessFeedCommentReplyModel } from "./BusinessFeedCommentReplyModel";

export const FeedItemCommentReplyUI = observer(
  ({ reply }: { reply: BusinessFeedCommentReplyModel }) => {
    const { t } = useTranslation();

    const author = reply.author;
    const [isEditMode, setIsEditMode] = React.useState(false);
    const [showReactionSheet, setShowReactionSheet] = React.useState(false);
    const [showManageSheet, setShowManageSheet] = React.useState(false);
    const getDisplayInfo = useDisplayNameInfo();

    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      const displayInfo = getDisplayInfo(author);

      const items: InfoRowItemProps[] = [
        {
          icon: User,
          text:
            (displayInfo?.text ? `(${displayInfo?.text}) ` : "") +
            (author?.displayName ?? ""),
        },
        {
          icon: Calendar,
          text: getDayJs(reply.data.ts),
        },
      ];

      reply.reactionRowItemCounts.forEach((reaction, _index) => {
        const Icon = reaction.icon;

        items.push({ text: `${reaction.count}`, icon: Icon });
      });

      return items;
    }, [getDisplayInfo, author, reply]);

    if (!author) {
      return null;
    }

    if (reply.data.deleted) {
      return (
        <Body muted fontStyle={"italic"}>
          {t("business:feed_content_deleted")}
        </Body>
      );
    }

    return (
      <View width={"100%"}>
        <XStack
          width={"100%"}
          gap={"$medium"}
          onPress={(e: GestureResponderEvent) => {
            if (isEditMode) {
              return;
            }
            e.stopPropagation();
            setShowReactionSheet(true);
          }}
          cursor={"pointer"}
        >
          <UserPropsAvatar
            {...author}
            size={AvatarSize.Smaller}
            showBorder={reply.comment.feedItem.businessPlan.checkPermission(
              Permission.AvatarShowExtraBorder,
              author.userUid,
            )}
          />

          <YStack gap="$small" flex={1}>
            {reply.data.hidden && (
              <Body muted fontStyle={"italic"}>
                {t("business:feed_content_hidden")}
              </Body>
            )}

            {reply.data.edited && !reply.data.hidden && (
              <Body muted fontStyle={"italic"}>
                {t("business:feed_content_edited")}
              </Body>
            )}

            {!!reply.data.reported && !reply.data.hidden && reply.isAdmin && (
              <Body muted fontStyle={"italic"}>
                {t("business:feed_content_reported")}
              </Body>
            )}

            {!reply.data.hidden && !isEditMode && (
              <FeedTextWithMentions
                text={reply.data.text}
                mentions={reply.data.mentions}
                businessPlan={reply.comment.feedItem.businessPlan}
              />
            )}

            {isEditMode && (
              <FeedTextInput
                autoFocus
                businessPlan={reply.comment.feedItem.businessPlan}
                placeholder={t("business:feed_edit_reply")}
                placeholderFocused={t("feed_mention_tip")}
                defaultValue={reply.data.text}
                onSubmitEditing={async (e) => {
                  await reply.edit(e.nativeEvent.text);
                  setIsEditMode(false);
                }}
              />
            )}

            <InfoRow items={infoRowItems} />
          </YStack>

          <View
            padding={"$small"}
            onPress={(e: GestureResponderEvent) => {
              e.stopPropagation();
              setShowManageSheet(!showManageSheet);
            }}
            cursor={"pointer"}
          >
            <MoreVertical color={"$muted"} />
          </View>
        </XStack>

        <FeedItemReactionShareSheet
          sheetId={"feed_reply_reactions"}
          commentReplyItemUid={reply.uid}
          open={showReactionSheet}
          onOpenChange={setShowReactionSheet}
        />

        <FeedItemManageSheet
          sheetId={"feed_comment_reply_manage"}
          editOption={{
            show: reply.isAuthor,
            callback: () => setIsEditMode(!isEditMode),
          }}
          hideOption={{
            show: reply.isAdmin,
            isHidden: reply.data.hidden,
            callback: () => reply.saveEditAction("hidden"),
          }}
          deleteOption={{
            show: reply.isAdmin || reply.isAuthor,
            callback: () => reply.saveEditAction("deleted"),
          }}
          reportOption={{
            show: !reply.isAuthor,
            callback: () => reply.saveEditAction("reported"),
          }}
          open={showManageSheet}
          onOpenChange={setShowManageSheet}
        />
      </View>
    );
  },
);
